import { useEffect, useRef, type ReactNode } from 'react';

import { useI18n } from '../i18n/index.js';
import styles from './Sheet.module.css';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  lead?: string;
  children: ReactNode;
}

/**
 * 모달 시트. 모바일에서는 아래에서 올라오고, 넓은 화면에서는 가운데에 뜬다.
 *
 * 접근성으로 챙기는 것:
 * - Esc로 닫힌다
 * - 열려 있는 동안 뒤 배경이 스크롤되지 않는다
 * - 열릴 때 포커스가 안으로 들어가고, 닫으면 원래 자리로 돌아간다
 * - Tab이 시트 밖으로 새지 않는다
 */
export function Sheet({ open, onClose, title, lead, children }: SheetProps) {
  const { t } = useI18n();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    restoreFocusRef.current = document.activeElement as HTMLElement | null;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusables = () =>
      panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? ([] as unknown as NodeListOf<HTMLElement>);

    /*
     * 열리면 시트 안으로 포커스를 옮긴다. 안 그러면 키보드 사용자는 여전히
     * 뒤 화면을 돌아다니게 된다.
     *
     * ⚠️ **내용이 이미 포커스를 잡았으면 건드리지 않는다.** React는 자식 effect를
     * 먼저 돌리므로, 검색 시트처럼 안에서 입력창에 커서를 두는 경우 여기서
     * 덮어쓰면 그 의도가 매번 무효가 된다(실제로 그랬다 — 검색창 대신 ✕에
     * 커서가 갔다).
     *
     * 아무도 안 잡았으면 **첫 버튼이 아니라 패널 자체**에 준다. 첫 버튼은 보통
     * ✕라서, 열자마자 Enter를 누르면 시트가 닫혀버린다. 패널은 tabIndex=-1이라
     * Tab을 누르면 자연스럽게 첫 항목으로 넘어간다.
     */
    if (panelRef.current?.contains(document.activeElement) !== true) {
      panelRef.current?.focus();
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      // 포커스가 시트를 벗어나지 않게 가둔다
      const items = [...focusables()];
      if (items.length === 0) {
        return;
      }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (firstItem === undefined || lastItem === undefined) {
        return;
      }
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;

      /*
       * 닫히면 열기 전 자리로 커서를 돌려준다 — **갈 곳을 잃었을 때만.**
       *
       * 포커스를 쥔 요소가 사라지면 브라우저가 포커스를 `body`로 떨어뜨린다.
       * 그 경우에만 되돌리면 된다.
       *
       * ⚠️ 무조건 되돌리면 **다음 시트의 커서를 빼앗는다.** 카테고리를 고르면
       * 검색 시트가 닫히면서 편집 시트가 같은 커밋에서 열리는데, 편집 시트의
       * 이름 칸은 이미 커서를 받은 뒤다(autoFocus는 커밋 중에 걸린다).
       * 여기서 되돌려 버리면 커서가 "추가" 버튼으로 끌려가고, 이어서 편집
       * 시트가 "안에 아무도 없네" 하고 패널을 잡는다 — 이름 칸에 커서가
       * 없는 채로 열린다. 실제로 그랬다.
       */
      const landed = document.activeElement;
      if (landed === null || landed === document.body) {
        restoreFocusRef.current?.focus();
      }
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div
      className={styles.backdrop}
      onMouseDown={(event) => {
        // 배경을 눌렀을 때만 닫는다. 시트 안에서 드래그하다 배경에서 떼는
        // 경우까지 닫히면 입력하던 내용이 날아간다.
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className={styles.head}>
          <div>
            <h2 className={styles.title}>{title}</h2>
            {lead !== undefined ? <p className={styles.lead}>{lead}</p> : null}
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>
        <div className={styles.body}>{children}</div>
      </div>
    </div>
  );
}
